/**
 * Employee PII Page — Sensitive info management
 *
 * Route: /employees/:id/sensitive-info
 * Requires pii.vault.view minimum to see the section.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Eye, EyeOff, Upload, Download, Trash2, Shield, FileText, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/Card';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { get, post, del } from '@/lib/api';

interface PIIMetadata {
  employee_id: string;
  employee_name: string;
  has_ssn: boolean;
  has_drivers_license: boolean;
  has_passport: boolean;
  has_insurance: boolean;
  has_bank_info: boolean;
  documents_count: number;
  i9_verified: boolean;
  i9_verified_at?: string;
  consent_given: boolean;
  consent_given_at?: string;
  dek_initialized: boolean;
}

interface PIIDocument {
  id: string;
  document_type: string;
  document_label: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  expiration_date?: string;
  verified: boolean;
  verified_by?: string;
  verified_at?: string;
  created_at: string;
}

interface AuditEntry {
  id: string;
  action: string;
  actor_id: string;
  actor_name: string;
  field?: string;
  document_id?: string;
  success: boolean;
  ip_address?: string;
  timestamp: string;
}

type RevealedField = 'ssn' | 'date_of_birth' | 'drivers_license_number' | 'passport_number' | 'bank_account_number';

export function EmployeePIIPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'info' | 'documents' | 'audit'>('info');
  const [revealedFields, setRevealedFields] = useState<Record<string, string>>({});
  const [revealTimers, setRevealTimers] = useState<Record<string, ReturnType<typeof setTimeout>>>({});
  const [uploading, setUploading] = useState(false);
  const [initializing, setInitializing] = useState(false);

  const { data: pii, loading, error, refetch } = useApi<PIIMetadata>(`pii/${id}`);
  const { data: docsData, refetch: refetchDocs } = useApi<{ documents: PIIDocument[] }>(`pii/${id}/documents`);
  const { data: auditData } = useApi<{ audit: AuditEntry[] }>(`pii/audit/${id}`);

  // Auto-mask revealed fields after 30 seconds
  const revealField = useCallback(async (field: RevealedField) => {
    try {
      const result = await get<{ fields: Record<string, string> }>(`pii/${id}/decrypt?fields=${field}`);
      setRevealedFields(prev => ({ ...prev, [field]: result.fields[field] ?? '' }));

      // Clear any existing timer
      if (revealTimers[field]) clearTimeout(revealTimers[field]);

      // Auto-mask after 30s
      const timer = setTimeout(() => {
        setRevealedFields(prev => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }, 30000);
      setRevealTimers(prev => ({ ...prev, [field]: timer }));
    } catch {
      // Permission denied or decrypt failed
    }
  }, [id, revealTimers]);

  const hideField = useCallback((field: string) => {
    setRevealedFields(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    if (revealTimers[field]) clearTimeout(revealTimers[field]);
  }, [revealTimers]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(revealTimers).forEach(t => clearTimeout(t));
    };
  }, [revealTimers]);

  const initializeVault = async () => {
    setInitializing(true);
    try {
      await post(`pii/initialize/${id}`, {});
      await refetch();
    } finally {
      setInitializing(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const docType = prompt('Document type (e.g., drivers_license_front, passport, i9_form, w4_form, insurance_card):');
    if (!docType) return;
    const label = prompt('Document label:') ?? file.name;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);
      formData.append('document_label', label);

      await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/pii/${id}/documents`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      await refetchDocs();
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (docId: string) => {
    if (!confirm('Delete this document permanently?')) return;
    await del(`pii/${id}/documents/${docId}`);
    await refetchDocs();
  };

  const downloadDocument = async (docId: string, fileName: string) => {
    const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/pii/${id}/documents/${docId}/download`, {
      credentials: 'include',
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-muted-foreground p-8">Loading...</div>;

  // Vault not initialized
  if (error || !pii?.dek_initialized) {
    return (
      <div className="space-y-6">
        <button onClick={() => navigate(`/employees/${id}`)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back to Employee
        </button>
        <Card>
          <div className="p-8 text-center space-y-4">
            <Lock className="w-12 h-12 text-muted-foreground mx-auto" />
            <h2 className="text-lg font-semibold">PII Vault Not Initialized</h2>
            <p className="text-muted-foreground">The secure vault for this employee's sensitive information has not been set up yet.</p>
            <button onClick={initializeVault} disabled={initializing} className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 disabled:opacity-50">
              {initializing ? 'Initializing...' : 'Initialize PII Vault'}
            </button>
          </div>
        </Card>
      </div>
    );
  }

  const formatSSN = (value: string) => {
    if (value.length === 9) return `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5)}`;
    return value;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(`/employees/${id}`)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back to Employee
        </button>
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-medium text-amber-600">PII Vault — All access is logged</span>
        </div>
      </div>

      <h1 className="text-xl font-semibold flex items-center gap-2">
        <Lock className="w-5 h-5" />
        {pii.employee_name} — Sensitive Information
      </h1>

      {/* Consent & I-9 Status */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <div className="p-4 flex items-center gap-3">
            {pii.consent_given
              ? <CheckCircle className="w-5 h-5 text-green-500" />
              : <AlertTriangle className="w-5 h-5 text-amber-500" />}
            <div>
              <div className="font-medium">Consent</div>
              <div className="text-sm text-muted-foreground">
                {pii.consent_given ? `Given ${pii.consent_given_at ? new Date(pii.consent_given_at).toLocaleDateString() : ''}` : 'Not yet provided'}
              </div>
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4 flex items-center gap-3">
            {pii.i9_verified
              ? <CheckCircle className="w-5 h-5 text-green-500" />
              : <Clock className="w-5 h-5 text-amber-500" />}
            <div>
              <div className="font-medium">I-9 Verification</div>
              <div className="text-sm text-muted-foreground">
                {pii.i9_verified ? `Verified ${pii.i9_verified_at ? new Date(pii.i9_verified_at).toLocaleDateString() : ''}` : 'Not verified'}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {(['info', 'documents', 'audit'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {tab === 'info' ? 'PII Fields' : tab === 'documents' ? `Documents (${pii.documents_count})` : 'Audit Trail'}
          </button>
        ))}
      </div>

      {/* PII Fields Tab */}
      {activeTab === 'info' && (
        <div className="space-y-3">
          <PIIFieldRow label="Social Security Number" hasValue={pii.has_ssn}
            revealed={revealedFields['ssn']} format={formatSSN}
            onReveal={() => revealField('ssn')} onHide={() => hideField('ssn')} />
          <PIIFieldRow label="Date of Birth" hasValue={true}
            revealed={revealedFields['date_of_birth']}
            onReveal={() => revealField('date_of_birth')} onHide={() => hideField('date_of_birth')} />
          <PIIFieldRow label="Driver's License" hasValue={pii.has_drivers_license}
            revealed={revealedFields['drivers_license_number']}
            onReveal={() => revealField('drivers_license_number')} onHide={() => hideField('drivers_license_number')} />
          <PIIFieldRow label="Passport" hasValue={pii.has_passport}
            revealed={revealedFields['passport_number']}
            onReveal={() => revealField('passport_number')} onHide={() => hideField('passport_number')} />
          <PIIFieldRow label="Bank Account" hasValue={pii.has_bank_info}
            revealed={revealedFields['bank_account_number']}
            onReveal={() => revealField('bank_account_number')} onHide={() => hideField('bank_account_number')} />
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <label className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 cursor-pointer text-sm flex items-center gap-2">
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading...' : 'Upload Document'}
              <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} accept="image/*,application/pdf" />
            </label>
          </div>

          {docsData?.documents.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">No documents uploaded yet.</div>
          )}

          <div className="grid gap-3">
            {docsData?.documents.map(doc => (
              <Card key={doc.id}>
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <div className="font-medium">{doc.document_label}</div>
                      <div className="text-xs text-muted-foreground">
                        {doc.document_type.replace(/_/g, ' ')} — {doc.file_name} — {(doc.file_size / 1024).toFixed(0)} KB
                      </div>
                      {doc.expiration_date && (
                        <div className="text-xs text-amber-600">Expires: {doc.expiration_date}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.verified && <StatusBadge status="completed" />}
                    <button onClick={() => downloadDocument(doc.id, doc.file_name)} className="p-2 hover:bg-muted rounded-md" title="Download">
                      <Download className="w-4 h-4" />
                    </button>
                    <button onClick={() => deleteDocument(doc.id)} className="p-2 hover:bg-destructive/10 text-destructive rounded-md" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Audit Tab */}
      {activeTab === 'audit' && (
        <div className="space-y-2">
          {auditData?.audit.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">No audit entries yet.</div>
          )}
          {auditData?.audit.map(entry => (
            <div key={entry.id} className="flex items-center justify-between py-2 px-3 border rounded-md text-sm">
              <div>
                <span className="font-medium">{entry.actor_name}</span>
                <span className="text-muted-foreground"> — {entry.action.replace(/_/g, ' ')}</span>
                {entry.field && <span className="text-muted-foreground"> ({entry.field})</span>}
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {entry.ip_address && <span>{entry.ip_address}</span>}
                <span>{new Date(entry.timestamp).toLocaleString()}</span>
                {entry.success ? <CheckCircle className="w-3 h-3 text-green-500" /> : <AlertTriangle className="w-3 h-3 text-red-500" />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── PIIFieldRow Component ─────────────────────────────────

function PIIFieldRow({ label, hasValue, revealed, format, onReveal, onHide }: {
  label: string;
  hasValue: boolean;
  revealed?: string;
  format?: (v: string) => string;
  onReveal: () => void;
  onHide: () => void;
}) {
  const displayValue = revealed
    ? (format ? format(revealed) : revealed)
    : (hasValue ? '••••••••' : 'Not provided');

  return (
    <Card>
      <div className="p-4 flex items-center justify-between">
        <div>
          <div className="text-sm text-muted-foreground">{label}</div>
          <div className={`font-mono ${revealed ? 'text-foreground' : 'text-muted-foreground'}`}>{displayValue}</div>
        </div>
        {hasValue && (
          <button onClick={revealed ? onHide : onReveal}
            className="p-2 hover:bg-muted rounded-md" title={revealed ? 'Hide' : 'Reveal (30s auto-mask)'}>
            {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
    </Card>
  );
}
